// src/features/mensagens/components/lista/MensagensLista.jsx
// ============================================================================
// 📋 MENSAGENS LISTA — Contenedor Principal Tipo WhatsApp
// 📦 V4 ENTERPRISE — Fix conversación inicial · WhatsApp-Grade
// ============================================================================
//
// REGLAS DE ORO:
// 1. Mobile-First: Diseñado para sentirse como app nativa
// 2. Pull-to-refresh con indicador de progreso
// 3. Search bar con debounce y filtros rápidos
// 4. Empty state contextual (sin mensajes, sin resultados, error)
// 5. Transiciones fluidas entre estados
// 6. V2: NovoContatoModal para buscar/crear clientes al iniciar conversación
// 7. V3: Drag & drop para reordenar conversaciones
// 8. V4: Fix — handleSelectClient asegura conversación antes de navegar
// ============================================================================

import React, { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  SlidersHorizontal,
  RefreshCw,
  Archive,
  Star,
  MessageSquare,
  Users,
  AlertCircle,
  Loader2,
  Inbox,
  ChevronDown,
  UserPlus,
  Trash2,
  LayoutList,
  LayoutGrid,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { useConversacoes } from '../../hooks/useConversacoes';
import { useMensagemAcoes } from '../../hooks/useMensagemAcoes';
import { ConversaItem } from './ConversaItem';
import { SearchBar } from './SearchBar';
import { FilterTabs } from './FilterTabs';
import { EmptyState } from './EmptyState';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { NovoContatoModal } from '../actions/NovoContatoModal';
// 🔥 V4: Importar servicio de mensajes para inicializar conversación
import { getMensagensService } from '../../api/mensagensService';
import {
  MESSAGE_FILTERS,
  PERFORMANCE,
  UI_DIMENSIONS,
} from '../../utils/constants';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Lista de conversaciones estilo WhatsApp
 * Contiene: Header + SearchBar + FilterTabs + Lista + EmptyState + NovoContatoModal
 * V4: Fix — handleSelectClient asegura conversación antes de navegar
 */
const MensagensLista = () => {
  // --------------------------------------------------------------------------
  // 📍 HOOKS
  // --------------------------------------------------------------------------
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // --------------------------------------------------------------------------
  // 🎣 HOOKS DE DATOS
  // --------------------------------------------------------------------------
  const {
    conversations,
    allConversations,
    stats,
    loading,
    refreshing,
    error,
    search,
    setSearch,
    filter,
    setFilter,
    refresh,
    getConversationMessages,
    formatForUI,
    isEmpty,
    hasUnread,
    filterOptions,
  } = useConversacoes({
    initialFilter: MESSAGE_FILTERS.ALL,
    pollingInterval: PERFORMANCE.STALE_TIME_MS,
  });

  const {
    actionInProgress,
    selectionMode,
    selectedIds,
    deleteMessage,
    deleteConversation,
    undoDelete,
    toggleArchive,
    toggleStar,
    markAllAsRead,
    copyPhone,
    enterSelectionMode,
    exitSelectionMode,
    toggleSelection,
    selectAll,
    deleteSelected,
    markSelectedAsRead,
    latestUndo,
    hasUndo,
  } = useMensagemAcoes({
    onRefresh: refresh,
  });

  // --------------------------------------------------------------------------
  // 📦 ESTADO LOCAL
  // --------------------------------------------------------------------------
  const [showSearch, setShowSearch] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showUndoBar, setShowUndoBar] = useState(false);
  const [showNovoContatoModal, setShowNovoContatoModal] = useState(false);
  
  // 🔄 Drag & Drop state
  const [layoutMode, setLayoutMode] = useState('vertical'); // 'vertical' | 'horizontal'
  const [activeDragId, setActiveDragId] = useState(null);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  // --------------------------------------------------------------------------
  // 🔄 UNDO TIMER
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (hasUndo && latestUndo) {
      setShowUndoBar(true);
      const timer = setTimeout(() => setShowUndoBar(false), 4500);
      return () => clearTimeout(timer);
    }
    setShowUndoBar(false);
  }, [hasUndo, latestUndo]);

  // --------------------------------------------------------------------------
  // 🎮 SENSORES DE DRAG & DROP
  // --------------------------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { 
      activationConstraint: { distance: 5 } 
    }),
    useSensor(TouchSensor, { 
      activationConstraint: { 
        delay: 200, 
        tolerance: 5 
      } 
    })
  );

  // --------------------------------------------------------------------------
  // 🎯 HANDLER: DRAG START
  // --------------------------------------------------------------------------
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveDragId(active.id);
    
    // Touch feedback háptico en mobile
    if (isMobile && navigator.vibrate) {
      navigator.vibrate(10);
    }
  }, [isMobile]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLER: DRAG END (Reordenar)
  // --------------------------------------------------------------------------
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    setActiveDragId(null);
    
    if (!over || active.id === over.id) return;
    
    setConversations(prev => {
      const oldIndex = prev.findIndex(c => c.id === active.id);
      const newIndex = prev.findIndex(c => c.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return prev;
      
      const result = [...prev];
      const [removed] = result.splice(oldIndex, 1);
      result.splice(newIndex, 0, removed);
      
      return result;
    });
  }, []);

  // --------------------------------------------------------------------------
  // 🎯 HANDLER: DRAG CANCEL
  // --------------------------------------------------------------------------
  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE NAVEGACIÓN
  // --------------------------------------------------------------------------

  const handleConversationPress = useCallback((conversation) => {
    navigate(`/mensagens/${conversation.clientId}`);
  }, [navigate]);

  const handleNewMessage = useCallback(() => {
    setShowNovoContatoModal(true);
  }, []);

  // 🔥 V4: handleSelectClient — asegura que el cliente tenga conversación antes de navegar
  const handleSelectClient = useCallback(async (client) => {
    if (!client?.id) return;
    
    try {
      // Inicializar el servicio de mensajes para asegurar que la conversación existe
      const userId = JSON.parse(localStorage.getItem('auth_user') || '{}')?.id || '1';
      const service = getMensagensService(userId);
      
      // Verificar si ya hay conversación
      const existingMessages = await service.getMessagesByClientId(client.id);
      
      // Si no hay mensajes, la conversación igual se abrirá (V4 del hook lo maneja)
      console.log(`📋 [V4] Conversación para ${client.name}: ${existingMessages.length} mensajes existentes`);
      
    } catch (err) {
      console.warn('⚠️ [V4] Error verificando conversación:', err.message);
      // No bloqueamos — el hook V4 igual cargará el chat
    }
    
    // Cerrar modal y navegar
    setShowNovoContatoModal(false);
    navigate(`/mensagens/${client.id}`);
  }, [navigate]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE ACCIONES
  // --------------------------------------------------------------------------

  const handleDelete = useCallback(async (conversationId) => {
    await deleteConversation(conversationId);
  }, [deleteConversation]);

  const handleArchive = useCallback(async (conversationId, archive) => {
    await toggleArchive(conversationId, archive);
  }, [toggleArchive]);

  const handleStar = useCallback(async (conversationId, star) => {
    await toggleStar(conversationId, star);
  }, [toggleStar]);

  const handleLongPress = useCallback((conversationId) => {
    enterSelectionMode();
    toggleSelection(conversationId);
  }, [enterSelectionMode, toggleSelection]);

  const handleSelectAll = useCallback(() => {
    const allIds = conversations.map(c => c.id);
    selectAll(allIds);
  }, [conversations, selectAll]);

  const handleUndo = useCallback(async () => {
    if (latestUndo) {
      await undoDelete(latestUndo.id);
    }
  }, [latestUndo, undoDelete]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE BÚSQUEDA
  // --------------------------------------------------------------------------

  const handleSearchFocus = useCallback(() => {
    setShowSearch(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearch(false);
    setSearch('');
  }, [setSearch]);

  // --------------------------------------------------------------------------
  // 📊 ESTADÍSTICAS DEL HEADER
  // --------------------------------------------------------------------------

  const headerStats = useMemo(() => [
    { label: 'Total', value: stats.total, icon: MessageSquare, color: 'text-blue-600' },
    { label: 'Não lidas', value: stats.unread, icon: AlertCircle, color: 'text-green-600' },
    { label: 'Favoritas', value: stats.starred, icon: Star, color: 'text-amber-500' },
  ], [stats]);

  // --------------------------------------------------------------------------
  // 🎨 DATOS FORMATEADOS PARA RENDER
  // --------------------------------------------------------------------------
  
  const formattedConversations = useMemo(() => 
    conversations.map(c => formatForUI(c)),
    [conversations, formatForUI]
  );

  // Encontrar la conversación activa para el DragOverlay
  const activeConversation = useMemo(() => 
    activeDragId 
      ? formattedConversations.find(c => c.id === activeDragId)
      : null,
    [activeDragId, formattedConversations]
  );

  // --------------------------------------------------------------------------
  // 🎨 RENDER: ESTADO DE CARGA
  // --------------------------------------------------------------------------

  if (loading && !refreshing && conversations.length === 0) {
    return (
      <div className="flex flex-col h-full bg-card">
        <Header
          stats={stats}
          isMobile={isMobile}
          onNewMessage={handleNewMessage}
          onSearchFocus={handleSearchFocus}
          selectionMode={selectionMode}
          selectedCount={selectedIds.size}
          onExitSelection={exitSelectionMode}
          onSelectAll={handleSelectAll}
          onDeleteSelected={deleteSelected}
          onMarkReadSelected={markSelectedAsRead}
          layoutMode={layoutMode}
          onToggleLayout={setLayoutMode}
        />
        <SkeletonLoader count={8} />
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER: ERROR
  // --------------------------------------------------------------------------

  if (error && conversations.length === 0) {
    return (
      <div className="flex flex-col h-full bg-card">
        <Header
          stats={stats}
          isMobile={isMobile}
          onNewMessage={handleNewMessage}
          onSearchFocus={handleSearchFocus}
          layoutMode={layoutMode}
          onToggleLayout={setLayoutMode}
        />
        <div className="flex-1 flex items-center justify-center p-8">
          <EmptyState
            icon={AlertCircle}
            title="Erro ao carregar"
            description={error}
            action={{
              label: 'Tentar novamente',
              onClick: refresh,
              icon: RefreshCw,
            }}
          />
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER PRINCIPAL
  // --------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-card relative">
      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <Header
        stats={stats}
        isMobile={isMobile}
        onNewMessage={handleNewMessage}
        onSearchFocus={handleSearchFocus}
        selectionMode={selectionMode}
        selectedCount={selectedIds.size}
        onExitSelection={exitSelectionMode}
        onSelectAll={handleSelectAll}
        onDeleteSelected={deleteSelected}
        onMarkReadSelected={markSelectedAsRead}
        layoutMode={layoutMode}
        onToggleLayout={setLayoutMode}
      />

      {/* ================================================================ */}
      {/* SEARCH BAR (Animada) */}
      {/* ================================================================ */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <SearchBar
                ref={searchInputRef}
                value={search}
                onChange={setSearch}
                onClose={handleSearchClose}
                placeholder="Buscar conversas..."
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================================================================ */}
      {/* FILTER TABS */}
      {/* ================================================================ */}
      <FilterTabs
        options={filterOptions}
        activeFilter={filter}
        onChange={setFilter}
      />

      {/* ================================================================ */}
      {/* BARRA UNDO (Estilo Gmail/WhatsApp) */}
      {/* ================================================================ */}
      <AnimatePresence>
        {showUndoBar && hasUndo && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="absolute top-0 left-0 right-0 z-30 mx-4 mt-2"
          >
            <div className="bg-gray-900 text-white rounded-xl px-4 py-3 shadow-lg flex items-center justify-between">
              <span className="text-sm">Conversa excluída</span>
              <button
                onClick={handleUndo}
                className="text-blue-400 font-semibold text-sm hover:text-blue-300 transition-colors px-3 py-1 rounded-lg hover:bg-gray-800"
              >
                Desfazer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================================================================ */}
      {/* LISTA DE CONVERSACIONES CON DRAG & DROP */}
      {/* ================================================================ */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={formattedConversations.map(c => c.id)}
          strategy={
            layoutMode === 'horizontal' 
              ? rectSortingStrategy 
              : verticalListSortingStrategy
          }
        >
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Pull-to-refresh indicator (mobile nativo) */}
            {refreshing && (
              <div className="flex items-center justify-center py-3 text-gray-400 gap-2">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-xs">Atualizando...</span>
              </div>
            )}

            {/* Lista */}
            <AnimatePresence mode="wait">
              {formattedConversations.length > 0 ? (
                <motion.div
                  key={`conversations-${layoutMode}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={
                    layoutMode === 'horizontal'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4'
                      : ''
                  }
                >
                  {formattedConversations.map((conversation) => (
                    <ConversaItem
                      key={conversation.id}
                      conversation={conversation}
                      isSelected={selectedIds.has(conversation.id)}
                      selectionMode={selectionMode}
                      onPress={handleConversationPress}
                      onLongPress={handleLongPress}
                      onArchive={handleArchive}
                      onDelete={handleDelete}
                      onStar={handleStar}
                      onToggleSelect={toggleSelection}
                      isDragging={activeDragId === conversation.id}
                      isOver={false} // Se maneja internamente en ConversaItem
                    />
                  ))}

                  {/* Padding inferior para scroll */}
                  <div className="h-4" />
                </motion.div>
              ) : (
                /* Empty State */
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-center h-full min-h-[300px] p-8"
                >
                  {search ? (
                    <EmptyState
                      icon={Search}
                      title="Nenhum resultado"
                      description={`Nenhuma conversa encontrada para "${search}"`}
                      action={{
                        label: 'Limpar busca',
                        onClick: () => setSearch(''),
                        icon: X,
                      }}
                    />
                  ) : filter === MESSAGE_FILTERS.UNREAD ? (
                    <EmptyState
                      icon={Inbox}
                      title="Tudo lido!"
                      description="Você não tem mensagens não lidas"
                    />
                  ) : filter === MESSAGE_FILTERS.ARCHIVED ? (
                    <EmptyState
                      icon={Archive}
                      title="Nenhuma conversa arquivada"
                      description="As conversas arquivadas aparecerão aqui"
                    />
                  ) : (
                    <EmptyState
                      icon={MessageSquare}
                      title="Nenhuma conversa"
                      description="Busque um cliente existente ou crie um novo contato"
                      action={{
                        label: 'Nova conversa',
                        onClick: handleNewMessage,
                        icon: UserPlus,
                      }}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </SortableContext>

        {/* ================================================================ */}
        {/* DRAG OVERLAY (Ítem flotante durante drag) */}
        {/* ================================================================ */}
        <DragOverlay>
          {activeConversation ? (
            <div className="opacity-90 scale-105 shadow-2xl rounded-lg">
              <ConversaItem
                conversation={activeConversation}
                isSelected={selectedIds.has(activeConversation.id)}
                selectionMode={false}
                isDragging={true}
                isOver={false}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* ================================================================ */}
      {/* MODAL NOVO CONTATO (Buscar cliente o crear nuevo) */}
      {/* ================================================================ */}
      <NovoContatoModal
        isOpen={showNovoContatoModal}
        onClose={() => setShowNovoContatoModal(false)}
        onSelectClient={handleSelectClient}
      />
    </div>
  );
};

// ============================================================================
// 🎨 SUBCOMPONENTE: HEADER
// ============================================================================

const Header = memo(({
  stats,
  isMobile,
  onNewMessage,
  onSearchFocus,
  selectionMode,
  selectedCount,
  onExitSelection,
  onSelectAll,
  onDeleteSelected,
  onMarkReadSelected,
  layoutMode = 'vertical',
  onToggleLayout,
}) => {
  // Modo selección
  if (selectionMode) {
    return (
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={onExitSelection}
            className="p-1 hover:bg-blue-500 rounded-lg transition-colors"
            aria-label="Fechar seleção"
          >
            <X size={20} />
          </button>
          <span className="font-semibold text-sm">
            {selectedCount} selecionada{selectedCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onSelectAll}
            className="px-3 py-1.5 text-xs font-medium hover:bg-blue-500 rounded-lg transition-colors"
          >
            Todas
          </button>
          <button
            onClick={onMarkReadSelected}
            className="p-2 hover:bg-blue-500 rounded-lg transition-colors"
            aria-label="Marcar como lidas"
          >
            <MessageSquare size={18} />
          </button>
          <button
            onClick={onDeleteSelected}
            className="p-2 hover:bg-red-500 rounded-lg transition-colors"
            aria-label="Excluir selecionadas"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Modo normal
  return (
    <div className="bg-card border-b border-gray-200 px-4 py-3">
      {/* Línea 1: Título + Acciones */}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-bold text-gray-900">
          Mensagens
        </h1>
        <div className="flex items-center gap-1">
          {/* Toggle de vista (solo desktop) */}
          {!isMobile && (
            <div className="flex items-center bg-muted rounded-xl p-1 mr-2">
              <button
                onClick={() => onToggleLayout?.('vertical')}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  layoutMode === 'vertical'
                    ? 'bg-card text-primary shadow-sm'
                    : 'text-muted hover:text-secondary'
                }`}
                aria-label="Vista en lista"
                title="Vista en lista"
              >
                <LayoutList size={16} />
                <span className="hidden sm:inline">Lista</span>
              </button>
              <button
                onClick={() => onToggleLayout?.('horizontal')}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  layoutMode === 'horizontal'
                    ? 'bg-card text-primary shadow-sm'
                    : 'text-muted hover:text-secondary'
                }`}
                aria-label="Vista en cards"
                title="Vista en cards"
              >
                <LayoutGrid size={16} />
                <span className="hidden sm:inline">Cards</span>
              </button>
            </div>
          )}

          {/* Buscar */}
          <button
            onClick={onSearchFocus}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Buscar"
          >
            <Search size={20} />
          </button>

          {/* Nueva conversación */}
          <button
            onClick={onNewMessage}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            aria-label="Nova conversa"
          >
            <UserPlus size={20} />
          </button>
        </div>
      </div>

      {/* Línea 2: Mini estadísticas */}
      <div className="flex gap-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <MessageSquare size={14} />
          <span>{stats.total} conversas</span>
        </div>
        {stats.unread > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span>{stats.unread} não lidas</span>
          </div>
        )}
        {stats.starred > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-amber-500">
            <Star size={14} className="fill-amber-400" />
            <span>{stats.starred}</span>
          </div>
        )}
      </div>
    </div>
  );
});

Header.displayName = 'MensagensListaHeader';

// ============================================================================
// 🏷️ DISPLAY NAME
// ============================================================================
MensagensLista.displayName = 'MensagensLista';

// ============================================================================
// 📤 EXPORTACIÓN
// ============================================================================

export { MensagensLista };
export default MensagensLista;